import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Users, GitMerge } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import DuplicatiDipendentiDialog from "@/components/DuplicatiDipendentiDialog";
import { validateIban, formatIban } from "@/lib/iban";

const empty = {
  nome: "",
  cognome: "",
  ruolo: "",
  iban: "",
  iban_note: "",
  attivo: true,
  cessato: false,
  cessato_data: "",
  cessato_motivo: "",
  mostra_nei_rapportini: true,
  aliases: [],
  has_account: false,
  can_view_all_rapportini: false,
  account_email: "",
  account_password: "",
};

export default function AnagraficaDipendenti() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [duplicatiOpen, setDuplicatiOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/employees");
      setItems(data || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Caricamento dipendenti non riuscito";
      toast.error(typeof msg === "string" ? msg : "Caricamento dipendenti non riuscito");
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) => {
    const q = search.toLowerCase();
    return (
      it.nome.toLowerCase().includes(q) ||
      it.cognome.toLowerCase().includes(q) ||
      (it.ruolo || "").toLowerCase().includes(q)
    );
  });
  // Iter 124: operativi vs licenziati/dimessi
  const operativi = filtered.filter((it) => !it.cessato);
  const cessati = filtered.filter((it) => !!it.cessato).sort((a, b) => (b.cessato_data || "").localeCompare(a.cessato_data || ""));
  const [showCessati, setShowCessati] = useState(false);
  const [ultimoGiorno, setUltimoGiorno] = useState(null);
  const fmtIt = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
  const MOTIVI = [["licenziato", "Licenziato"], ["dimesso", "Dimesso"], ["fine_contratto", "Fine contratto"], ["altro", "Altro"]];
  const toggleCessato = async (v) => {
    if (!v) { setForm((f) => ({ ...f, cessato: false, cessato_data: "", cessato_motivo: "", attivo: true })); return; }
    let last = null;
    if (editingId) {
      try { const { data } = await api.get(`/employees/${editingId}/ultimo-giorno`); last = data.ultimo_giorno; } catch (_) { /* ignore */ }
    }
    setUltimoGiorno(last);
    setForm((f) => ({ ...f, cessato: true, cessato_motivo: f.cessato_motivo || "licenziato", cessato_data: f.cessato_data || (last ? last.data : ""), attivo: false }));
  };

  const startCreate = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };
  const startEdit = (it) => {
    setForm({
      ...empty,
      ...it,
      aliases: it.aliases || [],
      mostra_nei_rapportini: it.mostra_nei_rapportini !== false,
      has_account: !!it.has_account,
      can_view_all_rapportini: !!it.can_view_all_rapportini,
      account_email: it.account_email || "",
      account_password: "",
      cessato: !!it.cessato,
      cessato_data: it.cessato_data || "",
      cessato_motivo: it.cessato_motivo || "",
    });
    setUltimoGiorno(null);
    setEditingId(it.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.nome.trim() || !form.cognome.trim()) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    // Validazione IBAN (opzionale): stato dalle prime 2 lettere + lunghezza
    const ibanCheck = validateIban(form.iban);
    if (!ibanCheck.valid) {
      toast.error(`IBAN non valido. ${ibanCheck.message}`);
      return;
    }
    if (form.cessato && !form.cessato_data) {
      toast.error("Indica la data di cessazione (il dipendente non ha ore registrate)");
      return;
    }
    const payload = { ...form, iban: ibanCheck.normalized };
    try {
      // Iter 31c — Step 3: prevenzione duplicati. Solo in fase di creazione
      // (non sull'edit), prima del POST, chiama /employees/check-name per
      // vedere se esiste già qualcuno con un nome molto simile. Se sì,
      // mostro un confirm con il candidato esatto/fuzzy più vicino.
      if (!editingId) {
        const fullName = `${form.cognome} ${form.nome}`.trim();
        try {
          const { data } = await api.post("/employees/check-name", {
            name: fullName,
            threshold: 0.85,
          });
          const ex = data?.exact;
          const fuzzy = (data?.fuzzy || [])[0];
          if (ex) {
            if (
              !window.confirm(
                `Esiste già un dipendente con questo nome:\n\n  • ${(ex.cognome || "")} ${(ex.nome || "")}\n\nCreare comunque un nuovo dipendente (rischio duplicati)?`
              )
            )
              return;
          } else if (fuzzy && fuzzy.employee) {
            const e = fuzzy.employee;
            if (
              !window.confirm(
                `Sembra che esista già un dipendente simile:\n\n  • ${(e.cognome || "")} ${(e.nome || "")} (somiglianza ${Math.round(fuzzy.similarity * 100)}%)\n\nVuoi creare comunque "${fullName}"? Se è la stessa persona, conviene chiudere e usare quella esistente.`
              )
            )
              return;
          }
        } catch (_) {
          /* se il check fallisce, lasciamo passare */
        }
      }
      if (editingId) await api.put(`/employees/${editingId}`, payload);
      else await api.post("/employees", payload);
      toast.success("Dipendente salvato");
      setOpen(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Salvataggio non riuscito";
      toast.error(typeof msg === "string" ? msg : "Salvataggio non riuscito");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Spostare il dipendente nel cestino?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Spostato nel cestino (recuperabile per 30 giorni)");
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 409) {
        if (window.confirm(
          `${detail}\n\nVuoi spostare comunque il dipendente nel cestino E rimuovere le righe dei rapportini collegati?\nIl dipendente resterà recuperabile per 30 giorni, ma le righe nei rapportini saranno perse.`
        )) {
          try {
            const res = await api.delete(`/employees/${id}?cascade=true`);
            toast.success(
              `Dipendente nel cestino. Aggiornati ${res.data?.rapportini_modified || 0} rapportini.`
            );
            load();
          } catch (e2) {
            console.error("[employees] cascade delete failed:", e2);
            toast.error("Eliminazione (cascade) non riuscita");
          }
        }
      } else {
        console.error("[employees] delete failed:", err);
        toast.error("Eliminazione non riuscita");
      }
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Anagrafica
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            Dipendenti
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} dipendenti registrati. L'autocomplete si attiva dopo 3 lettere.
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setDuplicatiOpen(true)}
              data-testid="trova-duplicati-button"
              className="h-12 border-amber-400 text-amber-800 hover:bg-amber-50"
            >
              <GitMerge className="h-5 w-5 mr-2" /> Trova duplicati
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={startCreate}
                  data-testid="add-dipendente-button"
                  className="h-12 bg-[#0044FF] hover:bg-[#0033CC]"
                >
                  <Plus className="h-5 w-5 mr-2" /> Nuovo dipendente
                </Button>
              </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingId ? "Modifica dipendente" : "Nuovo dipendente"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="font-bold">Cognome *</Label>
                  <Input
                    value={form.cognome}
                    onChange={(e) =>
                      setForm({ ...form, cognome: e.target.value })
                    }
                    className="h-12 mt-1"
                    data-testid="dipendente-cognome-input"
                  />
                </div>
                <div>
                  <Label className="font-bold">Nome *</Label>
                  <Input
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    className="h-12 mt-1"
                    data-testid="dipendente-nome-input"
                  />
                </div>
                <div>
                  <Label className="font-bold">Ruolo / Mansione</Label>
                  <Select
                    value={form.ruolo || ""}
                    onValueChange={(v) => setForm({ ...form, ruolo: v })}
                  >
                    <SelectTrigger
                      className="h-12 mt-1"
                      data-testid="dipendente-ruolo-select"
                    >
                      <SelectValue placeholder="Seleziona ruolo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Operaio">Operaio</SelectItem>
                      <SelectItem value="Capo cantiere">Capo cantiere (CC)</SelectItem>
                      <SelectItem value="PM">PM (Project Manager)</SelectItem>
                      <SelectItem value="Autista">Autista</SelectItem>
                      <SelectItem value="Impiegato">Impiegato</SelectItem>
                      <SelectItem value="Altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-bold">IBAN</Label>
                  <Input
                    value={form.iban || ""}
                    onChange={(e) =>
                      setForm({ ...form, iban: e.target.value.toUpperCase() })
                    }
                    placeholder="IT60 X054 2811 1010 0000 0123 456"
                    className="h-12 mt-1 font-mono tracking-wide"
                    data-testid="dipendente-iban-input"
                    autoComplete="off"
                  />
                  {(() => {
                    if (!(form.iban || "").trim())
                      return (
                        <p className="text-xs text-slate-400 mt-1">
                          Opzionale. Riconoscimento automatico dello stato dalle
                          prime 2 lettere (es. IT = Italia, 27 caratteri).
                        </p>
                      );
                    const c = validateIban(form.iban);
                    return (
                      <p
                        className={`text-xs mt-1 font-semibold ${
                          c.valid ? "text-emerald-700" : "text-red-600"
                        }`}
                        data-testid="dipendente-iban-feedback"
                      >
                        {c.valid ? `✓ ${c.message}` : `✗ ${c.message}`}
                      </p>
                    );
                  })()}
                  <Label className="font-bold text-xs mt-2 block">Nota IBAN (opzionale)</Label>
                  <Input
                    value={form.iban_note || ""}
                    onChange={(e) => setForm({ ...form, iban_note: e.target.value })}
                    placeholder="Es. conto intestato a un familiare"
                    className="h-10 mt-1 text-sm"
                    data-testid="dipendente-iban-note-input"
                    autoComplete="off"
                  />
                </div>
                <div className={`border rounded-md p-3 space-y-2 ${form.cessato ? "border-red-300 bg-red-50" : ""}`} data-testid="dipendente-cessato-box">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-sm">Licenziato / dimesso</div>
                      <div className="text-xs text-slate-500">Non compare più tra i dipendenti operativi (rapportini, programmi, controllo ore). Resta nello storico e nelle stampe.</div>
                    </div>
                    <Switch checked={!!form.cessato} onCheckedChange={toggleCessato} data-testid="dipendente-cessato-switch" />
                  </div>
                  {form.cessato && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs font-bold">Motivo</Label>
                        <Select value={form.cessato_motivo || "licenziato"} onValueChange={(v) => setForm({ ...form, cessato_motivo: v })}>
                          <SelectTrigger className="h-10" data-testid="dipendente-cessato-motivo"><SelectValue /></SelectTrigger>
                          <SelectContent>{MOTIVI.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold">{ultimoGiorno ? "Ultimo giorno lavorato" : "Data cessazione *"}</Label>
                        <Input type="date" value={form.cessato_data || ""} onChange={(e) => setForm({ ...form, cessato_data: e.target.value })} className="h-10" data-testid="dipendente-cessato-data" />
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {ultimoGiorno ? `Rilevato dai rapportini: ${fmtIt(ultimoGiorno.data)} (${ultimoGiorno.ore} h). Puoi correggerlo.` : "Nessuna ora registrata: indica tu la data."}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between border rounded-md p-3">
                  <div>
                    <div className="font-bold text-sm">Attivo</div>
                    <div className="text-xs text-slate-500">
                      Solo i dipendenti attivi compaiono nell'autocomplete
                    </div>
                  </div>
                  <Switch
                    checked={form.attivo}
                    onCheckedChange={(v) => setForm({ ...form, attivo: v })}
                    data-testid="dipendente-attivo-switch"
                  />
                </div>

                <div className="flex items-center justify-between border rounded-md p-3">
                  <div>
                    <div className="font-bold text-sm">Mostra nei rapportini</div>
                    <div className="text-xs text-slate-500">
                      Disattiva per nascondere dipendenti che non vanno mai nei
                      rapportini operativi (es. impiegati, tecnici d&apos;ufficio).
                      Resta visibile nell&apos;anagrafica e nei report.
                    </div>
                  </div>
                  <Switch
                    checked={form.mostra_nei_rapportini !== false}
                    onCheckedChange={(v) =>
                      setForm({ ...form, mostra_nei_rapportini: v })
                    }
                    data-testid="dipendente-mostra-nei-rapportini-switch"
                  />
                </div>

                {/* === Account & permessi === */}
                <div className="border rounded-md p-3 space-y-3 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-sm">Ha un account</div>
                      <div className="text-xs text-slate-500">
                        Permette al dipendente di accedere e compilare i propri rapportini
                      </div>
                    </div>
                    <Switch
                      checked={!!form.has_account}
                      onCheckedChange={(v) =>
                        setForm({
                          ...form,
                          has_account: v,
                          ...(v ? {} : { can_view_all_rapportini: false }),
                        })
                      }
                      data-testid="dipendente-has-account-switch"
                    />
                  </div>
                  {form.has_account && (
                    <div className="space-y-3 pt-2 border-t">
                      <div>
                        <Label className="text-xs font-bold">Email di login *</Label>
                        <Input
                          type="email"
                          value={form.account_email || ""}
                          onChange={(e) =>
                            setForm({ ...form, account_email: e.target.value })
                          }
                          placeholder="nome.cognome@azienda.it"
                          className="h-10 mt-1"
                          data-testid="dipendente-account-email"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-bold">
                          Password {editingId ? "(lascia vuoto per non cambiarla)" : "*"}
                        </Label>
                        <Input
                          type="password"
                          value={form.account_password || ""}
                          onChange={(e) =>
                            setForm({ ...form, account_password: e.target.value })
                          }
                          placeholder={editingId ? "•••••••• (non mostrata)" : "Min. 6 caratteri"}
                          className="h-10 mt-1"
                          data-testid="dipendente-account-password"
                          autoComplete="new-password"
                        />
                      </div>
                      <div className="flex items-center justify-between bg-white border rounded-md p-2.5">
                        <div>
                          <div className="font-bold text-sm">
                            Può vedere TUTTI i rapportini
                          </div>
                          <div className="text-xs text-slate-500">
                            Se disattivato (consigliato) vede solo i suoi.
                          </div>
                        </div>
                        <Switch
                          checked={!!form.can_view_all_rapportini}
                          onCheckedChange={(v) =>
                            setForm({ ...form, can_view_all_rapportini: v })
                          }
                          data-testid="dipendente-can-view-all-switch"
                        />
                      </div>
                    </div>
                  )}
                </div>


                <div>
                  <Label className="font-bold">Alias / Nomi alternativi</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Soprannomi o nomi errati con cui l'OCR potrebbe leggerlo dai
                    rapportini cartacei. L'AI userà questi alias per riconoscerlo.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(form.aliases || []).map((alias, i) => (
                      <span
                        key={alias}
                        className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-3 py-1 text-xs font-bold"
                        data-testid={`alias-chip-${i}`}
                      >
                        {alias}
                        <button
                          type="button"
                          onClick={() =>
                            setForm({
                              ...form,
                              aliases: form.aliases.filter((_, j) => j !== i),
                            })
                          }
                          className="hover:text-red-600 ml-1"
                          aria-label={`Rimuovi alias ${alias}`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {(form.aliases || []).length === 0 && (
                      <span className="text-xs text-slate-400 italic">
                        Nessun alias. Verranno aggiunti automaticamente quando confermi
                        nomi ambigui durante l'import OCR.
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Aggiungi alias e premi +"
                      className="h-10"
                      data-testid="dipendente-new-alias-input"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const v = e.target.value.trim();
                          if (v && !(form.aliases || []).some((a) => a.toLowerCase() === v.toLowerCase())) {
                            setForm({ ...form, aliases: [...(form.aliases || []), v] });
                            e.target.value = "";
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Annulla
                </Button>
                <Button
                  onClick={save}
                  data-testid="save-dipendente-button"
                  className="bg-[#0044FF] hover:bg-[#0033CC]"
                >
                  Salva
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <DuplicatiDipendentiDialog
        open={duplicatiOpen}
        onOpenChange={setDuplicatiOpen}
        onMerged={load}
      />

      <Card className="p-4">
        <Input
          placeholder="Cerca dipendente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-12"
          data-testid="search-dipendenti-input"
        />
      </Card>

      {operativi.length === 0 ? (
        <Card className="p-10 text-center">
          <Users className="h-10 w-10 mx-auto text-slate-300" />
          <div className="font-heading font-bold text-lg mt-3">
            Nessun dipendente
          </div>
          <p className="text-sm text-slate-500">
            Aggiungi il primo dipendente per iniziare.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {operativi.map((it) => (
            <Card
              key={it.id}
              className="p-4 flex items-center gap-3"
              data-testid={`dipendente-row-${it.id}`}
            >
              <div className="h-12 w-12 rounded-md bg-slate-100 grid place-items-center font-heading font-black text-base">
                {it.cognome[0]}
                {it.nome[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold truncate">
                  {it.cognome} {it.nome}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {it.ruolo || "—"} · {it.attivo ? "Attivo" : "Disattivato"}
                  {it.pending_confirmation && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 text-amber-700 font-bold"
                      data-testid={`pending-badge-${it.id}`}
                      title={`Inserito da ${it.created_by_name || "un utente"} — in attesa di conferma admin`}
                    >
                      · ⏳ da confermare
                    </span>
                  )}
                  {it.mostra_nei_rapportini === false && (
                    <span className="ml-2 inline-flex items-center gap-1 text-slate-500 font-bold">
                      · 🙈 nascosto nei rapportini
                    </span>
                  )}
                  {it.has_account && (
                    <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 font-bold">
                      · 🔐 account{it.can_view_all_rapportini ? " (vede tutto)" : ""}
                    </span>
                  )}
                  {it.iban && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 text-sky-700 font-bold"
                      title={formatIban(it.iban)}
                      data-testid={`iban-badge-${it.id}`}
                    >
                      · 🏦 IBAN
                    </span>
                  )}
                  {(it.aliases || []).length > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 text-amber-700">
                      · {it.aliases.length} alias OCR
                    </span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-10 w-10"
                    onClick={() => startEdit(it)}
                    data-testid={`edit-dipendente-${it.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-10 w-10 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => remove(it.id)}
                    data-testid={`delete-dipendente-${it.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {cessati.length > 0 && (
        <div className="space-y-2" data-testid="dipendenti-cessati-section">
          <button type="button" onClick={() => setShowCessati((v) => !v)} className="flex items-center gap-2 text-sm font-heading font-bold text-slate-600" data-testid="dipendenti-cessati-toggle">
            {showCessati ? "▾" : "▸"} Licenziati / dimessi ({cessati.length})
          </button>
          {showCessati && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {cessati.map((it) => (
                <Card key={it.id} className="p-4 flex items-center gap-3 bg-slate-50 border-slate-300" data-testid={`dipendente-cessato-row-${it.id}`}>
                  <div className="h-12 w-12 rounded-md bg-slate-200 grid place-items-center font-heading font-black text-base text-slate-500">{it.cognome[0]}{it.nome[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-slate-700 leading-snug">{it.cognome} {it.nome} <span className="font-normal text-slate-500 text-sm">(ultimo giorno {fmtIt(it.cessato_data) || "—"})</span></div>
                    <div className="text-xs text-slate-500 truncate">{MOTIVI.find(([k]) => k === it.cessato_motivo)?.[1] || "Cessato"}{it.cessato_data_auto ? " · data rilevata dai rapportini" : ""}{it.ruolo ? ` · ${it.ruolo}` : ""}</div>
                  </div>
                  {isAdmin && (
                    <Button size="icon" variant="outline" className="h-10 w-10" onClick={() => startEdit(it)} data-testid={`edit-dipendente-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
